// The reports and notifications calls (P6-E01, docs/API.md «Reports»), typed by the generated schema: the funnel
// and the publish success rate for the dashboard's «Воронка», the alerts feed for «Мои задачи», and the
// notifications behind the header bell. The MRR / ARR, AI spend and active-site counts stay on
// `GET /admin/reports/subscriptions`.
import { queryOptions } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';

/** One stage: organizations → research → variants → approved → demo → handoff → production. */
export type FunnelStage = Schemas['FunnelStage'];

/** GET /admin/reports/funnel (`report:read`, tenancy-scoped). */
export type FunnelReport = Schemas['FunnelReport'];

export const funnelQuery = queryOptions({
  queryKey: ['reports', 'funnel'] as const,
  queryFn: (): Promise<FunnelReport> => unwrap(api.GET('/admin/reports/funnel')),
  retry: false,
});

/** The demo → production share: production ÷ demo, null while no demo exists. */
export function demoToProduction(f: FunnelReport | undefined): { demo: number; production: number; share: number | null } | null {
  if (!f) return null;
  const demo = f.stages.find((x) => x.stage === 'demo')?.count ?? 0;
  const production = f.stages.find((x) => x.stage === 'production')?.count ?? 0;
  return { demo, production, share: demo ? production / demo : null };
}

/**
 * GET /admin/reports/publishing?days=30: deployments live ÷ (live + failed), null before anything finished; the
 * same per channel in `by_channel` (demo, production).
 */
export type PublishingReport = Schemas['PublishingReport'];

export const publishingQuery = queryOptions({
  queryKey: ['reports', 'publishing', 30] as const,
  queryFn: (): Promise<PublishingReport> => unwrap(api.GET('/admin/reports/publishing', { params: { query: { days: 30 } } })),
  retry: false,
});

/** An alert of the caller's organizations (the dashboard's, plus domains, integrations and AI budgets). */
export type AlertOut = Schemas['Alert'];

/** GET /admin/alerts: everything that needs attention («Мои задачи»). */
export const alertsQuery = queryOptions({
  queryKey: ['reports', 'alerts'] as const,
  queryFn: async (): Promise<AlertOut[]> => (await unwrap(api.GET('/admin/alerts'))).items,
  retry: false,
});

/** One notification: a standing alert (`at` null) or a job that finished in the last 7 days. */
export type NotificationOut = Schemas['Notification'];

/** GET /admin/notifications: newest first, unread since the bell was last opened. */
export type NotificationsOut = Schemas['NotificationsOut'];

export const notificationsKey = ['notifications'] as const;

export const notificationsQuery = queryOptions({
  queryKey: notificationsKey,
  queryFn: (): Promise<NotificationsOut> => unwrap(api.GET('/admin/notifications')),
  refetchInterval: 60_000,
  retry: false,
});

/** POST /admin/notifications/seen: the bell was opened, everything so far is read → the same list, read. */
export function markSeen(): Promise<NotificationsOut> {
  return unwrap(api.POST('/admin/notifications/seen'));
}
